import { useMemo, useState } from 'react';
import {
  campaignCopy,
  useAssignProducts,
  useCampaignHeaderQuery,
  useCampaignUiStore,
  useKeepPreviousProducts,
} from '@discovr/features';
import { Button } from '../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { ProductPickerList } from './ProductPickerList';
import { isProductsLocked, shouldOfferPreviousProducts, useCanResolvePreviousProducts, type ProductRow } from './previousProducts';

const copy = campaignCopy.creator.previousProducts;
const NONE_SELECTED: ReadonlySet<string> = new Set();

type ProductLite = { product_id: string; name: string };

// The backend refuses keep-previous below this; single-product campaigns have no product picker.
const MIN_CATALOG_SIZE = 2;

export function PreviousProductsPrompt({ row }: { row: ProductRow }) {
  const canResolve = useCanResolvePreviousProducts();
  const campaignId = useCampaignUiStore((s) => s.campaignId) ?? '';
  const offered = canResolve && shouldOfferPreviousProducts(row);
  const { data: header } = useCampaignHeaderQuery(campaignId, { enabled: offered });
  const catalog = useMemo(
    () => (header?.products ?? []).map((p) => ({ product_id: p.product_id, name: p.name })),
    [header?.products],
  );
  if (!offered || catalog.length < MIN_CATALOG_SIZE) return null;
  if (isProductsLocked(row)) return <span className="text-caption-regular text-muted-foreground">{copy.locked}</span>;
  return <Prompt row={row} campaignId={campaignId} catalog={catalog} />;
}

function Prompt({ row, campaignId, catalog }: { row: ProductRow; campaignId: string; catalog: ProductLite[] }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const keep = useKeepPreviousProducts(campaignId);
  const assign = useAssignProducts(campaignId);
  const names = (row.previous_assigned_products ?? []).map((p) => p.name).join(', ');
  const busy = keep.isPending || assign.isPending;

  return (
    <div className="flex flex-col gap-1.5 text-caption-regular text-muted-foreground">
      <p>{copy.prompt(names)}</p>
      <div className="flex items-center gap-1.5">
        <Button size="sm" variant="outline" disabled={busy} onClick={() => keep.mutate({ entryId: row.id })}>
          {copy.keep}
        </Button>
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <Button size="sm" variant="ghost" disabled={busy}>
              {copy.change}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-56 p-1.5">
            <ProductPickerList
              products={catalog}
              selectedIds={NONE_SELECTED}
              onToggle={(p) => assign.mutate({ entryId: row.id, productIds: [p.product_id] })}
              disabled={busy}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
